import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Mail, Mic, Headphones, RefreshCw, Loader2, Bot, Trash2, Plus, Bell, Sun, Moon, X, Pencil, CheckCircle2, Circle, Terminal, Github, Link2, Unlink, Lock, Globe, Check, Palette, Database, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  useIntegrations,
  useUpsertIntegration,
  useSyncIntegration,
  useDeleteIntegration,
  useUpdateIntegrationConfig,
} from "@/hooks/useIntegrations";
import { useGmailConnect } from "@/hooks/useEmails";
import { useAgentStatus } from "@/hooks/useAgentJobs";
import { useProjectRepos, useUpsertProjectRepo, useDeleteProjectRepo } from "@/hooks/useProjectRepos";
import { useGitHubConnect, useGitHubRepos } from "@/hooks/useGitHub";
import { useContacts, useCreateContact, useUpdateContact, useDeleteContact } from "@/hooks/useContacts";
import { useProjects } from "@/hooks/useProjects";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCommandStyle } from "@/contexts/CommandStyleContext";
import { CommandStyleSelector } from "@/components/settings/CommandStyleSelector";
import { WebhooksSection } from "@/components/settings/WebhooksSection";
import { BillingSection } from "@/components/settings/BillingSection";
import { BeeperCard } from "@/components/settings/BeeperCard";
import { getMuted, setMuted } from "@/lib/sounds";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import type { Integration, Contact, AgentStatus } from "@/lib/types";
import { formatError } from "@/lib/utils";

type IntegrationDef = {
  type: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  fields: { key: string; label: string; placeholder: string; type?: string }[];
};

// Non-Gmail integrations (Gmail has its own special card)
const OTHER_INTEGRATIONS: IntegrationDef[] = [
  {
    type: "fireflies",
    name: "Fireflies",
    description:
      "Extract action items from meeting transcripts automatically.",
    icon: <Mic className="h-5 w-5" />,
    fields: [
      {
        key: "api_key",
        label: "API Key",
        placeholder: "Go to app.fireflies.ai/integrations → API Key",
        type: "password",
      },
    ],
  },
  {
    type: "otter",
    name: "Otter.ai",
    description:
      "Sync meeting notes and transcripts from Otter.ai.",
    icon: <Mic className="h-5 w-5" />,
    fields: [
      {
        key: "api_key",
        label: "API Key",
        placeholder: "Go to otter.ai/settings → API",
        type: "password",
      },
    ],
  },
  {
    type: "google_meet",
    name: "Google Meet",
    description:
      "Pull meeting recordings and transcripts from Google Meet.",
    icon: <Mic className="h-5 w-5" />,
    fields: [
      {
        key: "enabled",
        label: "Status",
        placeholder: "Syncs automatically via your Google account",
      },
    ],
  },
  {
    type: "ms_teams",
    name: "Microsoft Teams",
    description:
      "Sync meeting transcripts and recordings from Teams.",
    icon: <Mic className="h-5 w-5" />,
    fields: [
      {
        key: "api_key",
        label: "App Token",
        placeholder: "Azure Portal → App Registrations → Client Secret",
        type: "password",
      },
    ],
  },
];

const DEV_INTEGRATIONS: IntegrationDef[] = [
  {
    type: "supabase_cli",
    name: "Supabase CLI",
    description:
      "Connect your Supabase account for deploying migrations and edge functions.",
    icon: <Database className="h-5 w-5" />,
    fields: [
      {
        key: "access_token",
        label: "Access Token",
        placeholder: "sbp_... — Get it at supabase.com/dashboard/account/tokens",
        type: "password",
      },
    ],
  },
  {
    type: "vercel",
    name: "Vercel",
    description:
      "Connect your Vercel account for deploying frontend builds.",
    icon: <Globe className="h-5 w-5" />,
    fields: [
      {
        key: "token",
        label: "Personal Token",
        placeholder: "Get it at vercel.com/account/tokens",
        type: "password",
      },
    ],
  },
];

function IntegrationCard({
  def,
  existing,
}: {
  def: IntegrationDef;
  existing: Integration | undefined;
}) {
  const upsert = useUpsertIntegration();
  const sync = useSyncIntegration();

  const [enabled, setEnabled] = useState(existing?.enabled ?? false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (existing?.config) {
      const initial: Record<string, string> = {};
      for (const field of def.fields) {
        const val = (existing.config as Record<string, unknown>)[field.key];
        initial[field.key] = typeof val === "string" ? val : "";
      }
      setFieldValues(initial);
      setEnabled(existing.enabled);
    }
  }, [existing, def.fields]);

  const handleSave = async () => {
    try {
      await upsert.mutateAsync({
        type: def.type,
        config: { ...fieldValues },
        enabled,
      });
      setDirty(false);
      toast.success(`${def.name} integration saved`);
    } catch (err) {
      toast.error(`Failed to save: ${formatError(err)}`);
    }
  };

  const handleSync = async () => {
    try {
      const result = await sync.mutateAsync(def.type);
      if (result?.error) {
        toast.error(result.error);
      } else {
        toast.success(
          result?.message ||
            `${def.name} synced${result?.items_created != null ? ` (${result.items_created} items created)` : ""}`
        );
      }
    } catch (err) {
      toast.error(`Sync failed: ${formatError(err)}`);
    }
  };

  const handleFieldChange = (key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    setDirty(true);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">{def.icon}</div>
            <div>
              <CardTitle className="text-base">{def.name}</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                {def.description}
              </CardDescription>
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={handleToggle} />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {def.fields.map((field) => (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={`${def.type}-${field.key}`} className="text-sm">
              {field.label}
            </Label>
            <Input
              id={`${def.type}-${field.key}`}
              type={field.type || "text"}
              placeholder={field.placeholder}
              value={fieldValues[field.key] || ""}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
            />
          </div>
        ))}

        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-muted-foreground">
            {def.type === "supabase_cli" || def.type === "vercel"
              ? (existing?.enabled ? "Connected" : "")
              : existing?.last_synced_at
                ? `Last synced ${formatDistanceToNow(new Date(existing.last_synced_at), { addSuffix: true })}`
                : "Never synced"}
          </div>
          <div className="flex gap-2">
            {dirty && (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={upsert.isPending}
              >
                {upsert.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : null}
                Save
              </Button>
            )}
            {def.type !== "supabase_cli" && def.type !== "vercel" && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSync}
                disabled={sync.isPending || !existing?.enabled}
              >
                {sync.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                Sync Now
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GmailAccountRow({ acct, onDisconnect }: { acct: Integration; onDisconnect: () => void }) {
  const config = acct.config as Record<string, string>;
  const email = config.email;
  const initialLabel = config.label?.trim() || "";
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(initialLabel);
  const updateConfig = useUpdateIntegrationConfig();
  const deleteIntegration = useDeleteIntegration();

  const save = async () => {
    const trimmed = draftLabel.trim();
    if (trimmed === initialLabel) {
      setEditing(false);
      return;
    }
    try {
      await updateConfig.mutateAsync({ id: acct.id, configPatch: { label: trimmed } });
      toast.success(trimmed ? `Renamed to "${trimmed}"` : "Label cleared");
      setEditing(false);
    } catch (err) {
      toast.error(`Failed to save: ${formatError(err)}`);
    }
  };

  const cancel = () => {
    setDraftLabel(initialLabel);
    setEditing(false);
  };

  const displayLabel = initialLabel || email.split("@")[0];

  return (
    <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 gap-2">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
        {editing ? (
          <div className="flex items-center gap-1 min-w-0 flex-1">
            <Input
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") cancel();
              }}
              placeholder={email.split("@")[0]}
              autoFocus
              className="h-7 text-sm"
              maxLength={40}
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0"
              onClick={save}
              disabled={updateConfig.isPending}
              title="Save"
            >
              {updateConfig.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0 text-muted-foreground"
              onClick={cancel}
              disabled={updateConfig.isPending}
              title="Cancel"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm font-medium truncate">{displayLabel}</span>
                <button
                  onClick={() => setEditing(true)}
                  className="text-muted-foreground hover:text-foreground shrink-0"
                  title="Rename"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground truncate">
                <span className="truncate">{email}</span>
                {acct.last_synced_at && (
                  <span className="shrink-0">· synced {formatDistanceToNow(new Date(acct.last_synced_at), { addSuffix: true })}</span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {!editing && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={onDisconnect}
          disabled={deleteIntegration.isPending}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function GmailCard({ gmailAccounts }: { gmailAccounts: Integration[] }) {
  const connect = useGmailConnect();
  const deleteIntegration = useDeleteIntegration();
  const sync = useSyncIntegration();

  const handleConnect = async () => {
    try {
      const data = await connect.mutateAsync(undefined);
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(`Failed to start OAuth: ${formatError(err)}`);
    }
  };

  const handleDisconnect = async (id: string, email: string) => {
    try {
      await deleteIntegration.mutateAsync(id);
      toast.success(`Disconnected ${email}`);
    } catch (err) {
      toast.error(`Failed to disconnect: ${formatError(err)}`);
    }
  };

  const handleSyncAll = async () => {
    try {
      const result = await sync.mutateAsync("gmail");
      if (result?.error) {
        toast.error(result.error);
      } else {
        toast.success(
          result?.message ||
            `Gmail synced${result?.emails_fetched != null ? ` (${result.emails_fetched} emails)` : ""}`
        );
      }
    } catch (err) {
      toast.error(`Sync failed: ${formatError(err)}`);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Gmail</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Connect Gmail accounts to view and reply to emails
              </CardDescription>
            </div>
          </div>
          {gmailAccounts.length > 0 && (
            <Badge variant="outline" className="text-xs">
              {gmailAccounts.length} connected
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {gmailAccounts.length > 0 && (
          <div className="space-y-2">
            {gmailAccounts.map((acct) => {
              const email = (acct.config as Record<string, unknown>).email as string;
              return (
                <GmailAccountRow
                  key={acct.id}
                  acct={acct}
                  onDisconnect={() => handleDisconnect(acct.id, email)}
                />
              );
            })}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={handleConnect}
            disabled={connect.isPending}
          >
            {connect.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Plus className="h-3 w-3 mr-1" />
            )}
            Connect Gmail Account
          </Button>
          {gmailAccounts.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleSyncAll}
              disabled={sync.isPending}
            >
              {sync.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              Sync
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CustomConnectionsSection() {
  const { user } = useAuth();
  const [connections, setConnections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", key: "" });
  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState<string | null>(null);

  const loadConnections = async () => {
    if (!user) return;
    const { data } = await (await import("@/lib/supabase")).supabase
      .from("custom_connections")
      .select("*")
      .eq("user_id", user.id)
      .order("name");
    setConnections(data || []);
    setLoading(false);
  };

  useEffect(() => { loadConnections(); }, [user]);

  const handleAdd = async () => {
    if (!user || !form.name.trim() || !form.url.trim() || !form.key.trim()) {
      toast.error("Name, URL, and key are required");
      return;
    }
    setSaving(true);
    try {
      const slug = form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      // adversarial-breaker F5.2 (Phase 5, 2026-05-17): foreign-Supabase
      // service-role key goes through store-custom-connection-secret, which
      // mints a vault.secrets entry and stores the secret_id on the row.
      // Previously this code inserted `config: { url, key }` plaintext —
      // any future RLS regression or DB dump would have leaked every
      // user's external credentials. CHECK constraint custom_connections_no_plain_key
      // (migration 00064) now blocks plaintext writes at the DB layer too.
      const { data, error } = await (await import("@/lib/supabase")).supabase.functions.invoke(
        "store-custom-connection-secret",
        {
          body: {
            name: form.name.trim(),
            slug,
            type: "supabase",
            url: form.url.trim(),
            key: form.key.trim(),
          },
        },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`${form.name} connected`);
      setForm({ name: "", url: "", key: "" });
      setShowAdd(false);
      await loadConnections();
      // Auto-discover schema
      if (data?.id) {
        handleDiscover(data.id, form.name.trim());
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscover = async (id: string, name: string) => {
    setDiscovering(id);
    try {
      const { data, error } = await (await import("@/lib/supabase")).supabase.functions.invoke("discover-schema", {
        body: { connection_id: id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Discovered ${data.tables} tables in ${name}`);
      await loadConnections();
    } catch (err) {
      toast.error(`Schema discovery failed: ${formatError(err)}`);
    } finally {
      setDiscovering(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await (await import("@/lib/supabase")).supabase
        .from("custom_connections")
        .delete()
        .eq("id", id);
      toast.success("Connection removed");
      await loadConnections();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <section className="mb-6">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
        Custom Connections
      </h2>
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="text-base">Connected Platforms</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Connect any Supabase project — the AI chat can read, write, and manage data in it
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : connections.length > 0 ? (
            <div className="space-y-2">
              {connections.map((conn) => {
                const schema = conn.schema_cache as { tables: string[] } | null;
                return (
                  <div key={conn.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p className="text-sm font-medium">{conn.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {schema ? `${schema.tables.length} tables` : "Schema not discovered"} · {conn.slug}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleDiscover(conn.id, conn.name)}
                        disabled={discovering === conn.id}
                      >
                        {discovering === conn.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(conn.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {showAdd ? (
            <div className="space-y-3 rounded-md border border-border p-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Connection Name</Label>
                <Input
                  placeholder="e.g. HELLA Sales, My CRM, Client Portal"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Supabase URL</Label>
                <Input
                  placeholder="https://xxxxx.supabase.co"
                  value={form.url}
                  onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Service Role Key</Label>
                <Input
                  type="password"
                  placeholder="eyJhbGci..."
                  value={form.key}
                  onChange={(e) => setForm(f => ({ ...f, key: e.target.value }))}
                />
                <p className="text-[10px] text-muted-foreground">
                  Supabase Dashboard → Settings → API → service_role key
                </p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={handleAdd} disabled={saving}>
                  {saving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                  Connect
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" className="w-full" onClick={() => setShowAdd(true)}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Connection
            </Button>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function ContactsSection() {
  const { user } = useAuth();
  const { data: contacts, isLoading, refetch: refetchContacts } = useContacts();
  const { data: projects } = useProjects();
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [contactsExpanded, setContactsExpanded] = useState(false);

  const handleSyncGoogle = async () => {
    if (!user) return;
    setSyncingGoogle(true);
    try {
      const { data, error } = await (await import("@/lib/supabase")).supabase.functions.invoke(
        "sync-google-contacts",
        { body: { user_id: user.id } }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Synced ${data.contacts_synced ?? 0} contacts from Google`);
      refetchContacts();
    } catch (err) {
      toast.error(`Google sync failed: ${formatError(err)}`);
    } finally {
      setSyncingGoogle(false);
    }
  };

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_tag: "",
    relationship: "",
    notes: "",
  });

  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", project_tag: "", relationship: "", notes: "" });
  };

  const startEdit = (contact: Contact) => {
    setEditingId(contact.id);
    setShowAddForm(false);
    setForm({
      name: contact.name,
      email: contact.email || "",
      phone: contact.phone || "",
      project_tag: contact.project_tag || "",
      relationship: contact.relationship || "",
      notes: contact.notes || "",
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      project_tag: form.project_tag || null,
      relationship: form.relationship.trim() || null,
      notes: form.notes.trim() || null,
    };

    try {
      if (editingId) {
        await updateContact.mutateAsync({ id: editingId, ...payload });
        toast.success("Contact updated");
        setEditingId(null);
      } else {
        await createContact.mutateAsync(payload);
        toast.success("Contact added");
        setShowAddForm(false);
      }
      resetForm();
    } catch (err) {
      toast.error(`Failed: ${formatError(err)}`);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteContact.mutateAsync(id);
      toast.success("Contact deleted");
    } catch (err) {
      toast.error(`Failed: ${formatError(err)}`);
    }
  };

  const activeProjects = (projects || []).filter((p) => p.status === "active");

  const contactForm = (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div className="space-y-1.5">
        <Label className="text-sm">Name</Label>
        <Input
          placeholder="John Doe"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-sm">Email</Label>
          <Input
            type="email"
            placeholder="john@example.com"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Phone</Label>
          <Input
            placeholder="+1 555-1234"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-sm">Project</Label>
          <Select
            value={form.project_tag || "__none__"}
            onValueChange={(v) => setForm((f) => ({ ...f, project_tag: v === "__none__" ? "" : v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {activeProjects.map((p) => (
                <SelectItem key={p.id} value={p.slug}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Relationship</Label>
          <Input
            placeholder="e.g. client, team, vendor"
            value={form.relationship}
            onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value }))}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm">Notes</Label>
        <Input
          placeholder="Optional notes"
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
        />
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={createContact.isPending || updateContact.isPending}
        >
          {(createContact.isPending || updateContact.isPending) && (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          )}
          {editingId ? "Save" : "Add"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setShowAddForm(false);
            setEditingId(null);
            resetForm();
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <section className="mb-6">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
        Contacts
      </h2>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">People</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Contacts for smarter AI categorization
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : contacts && contacts.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{contacts.length} contacts</p>
              {contacts.slice(0, contactsExpanded ? undefined : 5).map((contact) =>
                editingId === contact.id ? (
                  <div key={contact.id}>{contactForm}</div>
                ) : (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                  >
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p className="text-sm font-medium truncate">{contact.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[contact.email, contact.phone, contact.relationship, contact.project_tag]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground"
                        onClick={() => startEdit(contact)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(contact.id)}
                        disabled={deleteContact.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )
              )}
              {contacts.length > 5 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-muted-foreground"
                  onClick={() => setContactsExpanded(!contactsExpanded)}
                >
                  {contactsExpanded ? "Show less" : `Show all ${contacts.length} contacts`}
                </Button>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">No contacts yet.</p>
          )}

          {showAddForm && !editingId ? (
            contactForm
          ) : !editingId ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => setShowAddForm(true)}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Contact
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSyncGoogle}
                disabled={syncingGoogle}
              >
                {syncingGoogle ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                Sync from Google
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}

const STATUS_DOT: Record<string, string> = {
  online: "bg-green-500",
  busy: "bg-yellow-500",
  offline: "bg-gray-400",
  provisioning: "bg-blue-500 animate-pulse",
};

const STATUS_LABEL: Record<string, string> = {
  online: "Online",
  busy: "Busy",
  offline: "Offline",
  provisioning: "Provisioning...",
};

function GitHubRepoMappingCard({
  githubIntegration,
}: {
  githubIntegration: Integration | undefined;
}) {
  const { data: projects } = useProjects();
  const { data: repos, isLoading: reposLoading } = useProjectRepos();
  const upsertRepo = useUpsertProjectRepo();
  const deleteRepo = useDeleteProjectRepo();
  const deleteIntegration = useDeleteIntegration();
  const connect = useGitHubConnect();

  const isConnected = !!githubIntegration?.enabled;
  const ghConfig = githubIntegration?.config as
    | { access_token: string; username: string; avatar_url: string }
    | undefined;

  const {
    data: ghRepos,
    isLoading: ghReposLoading,
    refetch: refetchGhRepos,
  } = useGitHubRepos(isConnected);

  const [linkingRepo, setLinkingRepo] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [repoPath, setRepoPath] = useState("");
  const [searchFilter, setSearchFilter] = useState("");

  const handleConnect = async () => {
    try {
      const data = await connect.mutateAsync(undefined);
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(`Failed to start GitHub OAuth: ${formatError(err)}`);
    }
  };

  const handleDisconnect = async () => {
    if (!githubIntegration) return;
    try {
      await deleteIntegration.mutateAsync(githubIntegration.id);
      toast.success("GitHub disconnected");
    } catch (err) {
      toast.error(`Failed to disconnect: ${formatError(err)}`);
    }
  };

  const handleLinkRepo = async (fullName: string, defaultBranch: string) => {
    if (!selectedProject) {
      toast.error("Select a project first");
      return;
    }
    try {
      await upsertRepo.mutateAsync({
        project_slug: selectedProject,
        repo_path: repoPath || `G:/projects/${fullName.split("/").pop()}`,
        github_repo: fullName,
        default_branch: defaultBranch,
      });
      toast.success(`Linked ${fullName} to ${selectedProject}`);
      setLinkingRepo(null);
      setSelectedProject("");
      setRepoPath("");
    } catch (err) {
      toast.error(`Failed to link: ${formatError(err)}`);
    }
  };

  const handleUnlinkRepo = async (id: string) => {
    try {
      await deleteRepo.mutateAsync(id);
      toast.success("Repo unlinked");
    } catch (err) {
      toast.error(`Failed to unlink: ${formatError(err)}`);
    }
  };

  // Map of github_repo -> project_repo for quick lookup
  const linkedRepoMap = new Map(
    (repos || []).map((r) => [r.github_repo, r])
  );

  const activeProjects = (projects || []).filter(
    (p) => p.status === "active"
  );

  const filteredGhRepos = (ghRepos || []).filter((r) =>
    searchFilter
      ? r.full_name.toLowerCase().includes(searchFilter.toLowerCase())
      : true
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Github className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">GitHub Repos</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Connect GitHub and link repos to projects for the agent
              </CardDescription>
            </div>
          </div>
          {isConnected && ghConfig && (
            <div className="flex items-center gap-2">
              <img
                src={ghConfig.avatar_url}
                alt={ghConfig.username}
                className="h-6 w-6 rounded-full"
              />
              <span className="text-xs text-muted-foreground">
                {ghConfig.username}
              </span>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {!isConnected ? (
          /* --- Not connected: show Connect button --- */
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={handleConnect}
            disabled={connect.isPending}
          >
            {connect.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Github className="h-3.5 w-3.5 mr-1" />
            )}
            Connect to GitHub
          </Button>
        ) : (
          /* --- Connected: show linked repos + available repos --- */
          <>
            {/* Currently linked repos */}
            {reposLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : repos && repos.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Linked Repos
                </p>
                {repos.map((repo) => (
                  <div
                    key={repo.id}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                  >
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Link2 className="h-3 w-3 text-green-500 shrink-0" />
                        <p className="text-sm font-medium truncate">
                          {repo.github_repo}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        Project: <span className="text-foreground">{repo.project_slug}</span>
                        {" "}&middot;{" "}
                        <span className="font-mono">{repo.repo_path}</span>
                        {" "}&middot;{" "}
                        {repo.default_branch}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleUnlinkRepo(repo.id)}
                      disabled={deleteRepo.isPending}
                      title="Unlink repo"
                    >
                      <Unlink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}

            {/* Available GitHub repos */}
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Available Repos
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => refetchGhRepos()}
                  disabled={ghReposLoading}
                >
                  {ghReposLoading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                </Button>
              </div>

              {ghReposLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : ghRepos && ghRepos.length > 0 ? (
                <>
                  <Input
                    placeholder="Filter repos..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <div className="max-h-64 overflow-y-auto space-y-1 rounded-md border border-border p-1">
                    {filteredGhRepos.map((ghRepo) => {
                      const linked = linkedRepoMap.get(ghRepo.full_name);
                      const isLinking = linkingRepo === ghRepo.full_name;

                      return (
                        <div key={ghRepo.full_name}>
                          <div className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted/50">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {ghRepo.private ? (
                                <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
                              ) : (
                                <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
                              )}
                              <span className="text-sm truncate">
                                {ghRepo.full_name}
                              </span>
                            </div>
                            {linked ? (
                              <Badge
                                variant="outline"
                                className="text-[10px] text-green-500 border-green-500/30 shrink-0"
                              >
                                {linked.project_slug}
                              </Badge>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs shrink-0"
                                onClick={() => {
                                  if (isLinking) {
                                    setLinkingRepo(null);
                                  } else {
                                    setLinkingRepo(ghRepo.full_name);
                                    setSelectedProject("");
                                    setRepoPath("");
                                  }
                                }}
                              >
                                {isLinking ? "Cancel" : "Link"}
                              </Button>
                            )}
                          </div>

                          {/* Inline link form */}
                          {isLinking && (
                            <div className="mx-2 mb-2 p-2 space-y-2 rounded border border-border bg-muted/30">
                              <div className="space-y-1">
                                <Label className="text-xs">Project</Label>
                                <Select
                                  value={selectedProject}
                                  onValueChange={setSelectedProject}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue placeholder="Select a project..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {activeProjects.map((p) => (
                                      <SelectItem
                                        key={p.slug}
                                        value={p.slug}
                                      >
                                        {p.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">
                                  Local Path{" "}
                                  <span className="text-muted-foreground font-normal">
                                    (optional)
                                  </span>
                                </Label>
                                <Input
                                  className="h-8 text-sm font-mono"
                                  placeholder={`G:/projects/${ghRepo.name}`}
                                  value={repoPath}
                                  onChange={(e) => setRepoPath(e.target.value)}
                                />
                              </div>
                              <Button
                                size="sm"
                                className="w-full h-7 text-xs"
                                onClick={() =>
                                  handleLinkRepo(
                                    ghRepo.full_name,
                                    ghRepo.default_branch
                                  )
                                }
                                disabled={upsertRepo.isPending || !selectedProject}
                              >
                                {upsertRepo.isPending ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                  <Link2 className="h-3 w-3 mr-1" />
                                )}
                                Link to {selectedProject || "..."}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground py-2">
                  No repos found. Make sure the GitHub account has access to
                  repositories.
                </p>
              )}
            </div>

            {/* Disconnect button */}
            <div className="pt-2 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground hover:text-destructive"
                onClick={handleDisconnect}
                disabled={deleteIntegration.isPending}
              >
                <Unlink className="h-3 w-3 mr-1" />
                Disconnect GitHub
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function AddAgentSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState<"name" | "command" | "waiting">("name");
  const [machineName, setMachineName] = useState("");
  const [command, setCommand] = useState("");
  const [agentId, setAgentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const handleCreate = async () => {
    if (!machineName.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("provision-agent", {
        body: { action: "create", machine_name: machineName.trim() },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      setCommand(data.command);
      setAgentId(data.agent_id);
      setStep("command");
      queryClient.invalidateQueries({ queryKey: ["agent-status"] });
    } catch (err) {
      toast.error(`Failed: ${formatError(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast("Copied to clipboard");
  };

  const handleDone = () => {
    setStep("name");
    setMachineName("");
    setCommand("");
    setAgentId(null);
    onClose();
  };

  const { data: agents } = useAgentStatus();
  const provisionedAgent = agents?.find((a) => a.id === agentId);
  const isOnline = provisionedAgent?.status === "online" || provisionedAgent?.status === "busy";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleDone()}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>Add Agent</SheetTitle>
          <SheetDescription>
            {step === "name" && "Set up a Mac Mini as a headless AI agent."}
            {step === "command" && "Paste this command on your Mac Mini."}
            {step === "waiting" && "Waiting for the agent to come online..."}
          </SheetDescription>
        </SheetHeader>

        <div className="py-4 space-y-4">
          {step === "name" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="machine-name">Machine Name</Label>
                <Input
                  id="machine-name"
                  value={machineName}
                  onChange={(e) => setMachineName(e.target.value)}
                  placeholder="e.g. Dalin's Mac"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                />
              </div>
              <Button onClick={handleCreate} disabled={loading || !machineName.trim()} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Generate Setup Command
              </Button>
            </>
          )}

          {step === "command" && (
            <>
              <div className="relative">
                <pre className="bg-zinc-900 border border-border rounded-lg p-3 text-xs text-green-400 font-mono whitespace-pre-wrap break-all select-all">
                  {command}
                </pre>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCopy} variant="outline" className="flex-1">
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Terminal className="h-4 w-4 mr-2" />}
                  {copied ? "Copied" : "Copy Command"}
                </Button>
                <Button onClick={() => setStep("waiting")} className="flex-1">
                  I Pasted It
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center">
                This command expires in 1 hour. It can only be used once.
              </p>
            </>
          )}

          {step === "waiting" && (
            <div className="text-center py-6 space-y-4">
              {isOnline ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                    <Check className="h-8 w-8 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-500">Agent is online!</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {provisionedAgent?.machine_id} is connected and ready.
                    </p>
                  </div>
                  <Button onClick={handleDone} className="w-full">Done</Button>
                </>
              ) : (
                <>
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
                  <div>
                    <p className="text-sm text-muted-foreground">Setting up {machineName}...</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      This takes 3-5 minutes. The agent will appear here when ready.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AgentSection() {
  const [showAddAgent, setShowAddAgent] = useState(false);
  const { data: agents, isLoading: statusLoading } = useAgentStatus();
  const { data: integrations } = useIntegrations();

  const push = usePushNotifications();

  const githubIntegration = integrations?.find((i) => i.type === "github");

  return (
    <section className="mb-6">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
        Agents
      </h2>

      {/* Agent Status Cards */}
      {statusLoading ? (
        <Card className="mb-4">
          <CardHeader>
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          </CardHeader>
        </Card>
      ) : agents && agents.length > 0 ? (
        agents.map((agent) => (
          <Card key={agent.id} className="mb-3">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted">
                    <Bot className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{agent.machine_id}</CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      Claude Code agent
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className="flex items-center gap-1.5">
                  <span className={`inline-block h-2 w-2 rounded-full ${STATUS_DOT[agent.status] || STATUS_DOT.offline}`} />
                  {STATUS_LABEL[agent.status] || "Unknown"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-muted-foreground space-y-2">
                <div className="space-y-1">
                  <p>
                    Last heartbeat:{" "}
                    <span className="text-foreground">
                      {formatDistanceToNow(new Date(agent.last_heartbeat), { addSuffix: true })}
                    </span>
                  </p>
                  {agent.current_job_id && (
                    <p>Current job: <span className="text-foreground font-mono text-[11px]">{agent.current_job_id}</span></p>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-1 border-t border-border">
                  <span className={`inline-block h-2 w-2 rounded-full ${
                    agent.auth_status === "authenticated" ? "bg-green-500" :
                    agent.auth_status === "needs_login" ? "bg-red-500 animate-pulse" :
                    "bg-yellow-500"
                  }`} />
                  <span className="text-foreground">
                    {agent.auth_status === "authenticated" ? "Claude Max Connected" :
                     agent.auth_status === "needs_login" ? "Claude Max — Login Required" :
                     "Claude Auth — Checking..."}
                  </span>
                </div>
                {agent.auth_status === "needs_login" && (
                  <p className="text-[10px] text-red-400 leading-tight">
                    Sessions will use API credits instead of your Max subscription. SSH into {agent.machine_id} and run <code className="bg-muted px-1 py-0.5 rounded">claude /login</code> to reconnect.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      ) : null}

      {/* Add Agent button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowAddAgent(true)}
        className="w-full mb-4"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Agent
      </Button>

      <AddAgentSheet open={showAddAgent} onClose={() => setShowAddAgent(false)} />

      {/* Claude Max Connection */}
      <ClaudeMaxCard agents={agents || []} />

      {/* Push Notifications */}
      <Card className="mb-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <Bell className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base">Push Notifications</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Get notified when the agent completes a job
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={push.isSubscribed}
              disabled={!push.isSupported || push.isLoading}
              onCheckedChange={async (checked) => {
                if (checked) {
                  const ok = await push.subscribe();
                  if (ok) toast.success("Push notifications enabled");
                  else toast.error("Failed to enable notifications");
                } else {
                  await push.unsubscribe();
                  toast("Push notifications disabled");
                }
              }}
            />
          </div>
        </CardHeader>
        {!push.isSupported && (
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Push notifications are not supported in this browser or VAPID key is not configured.
            </p>
          </CardContent>
        )}
      </Card>

      {/* GitHub Repo Mappings */}
      <GitHubRepoMappingCard githubIntegration={githubIntegration} />
    </section>
  );
}

function ClaudeMaxCard({ agents }: { agents: AgentStatus[] }) {
  const [step, setStep] = useState<"idle" | "waiting" | "paste">("idle");
  const [codeVerifier, setCodeVerifier] = useState<string | null>(null);
  const [pasteCode, setPasteCode] = useState("");
  const [loading, setLoading] = useState(false);

  const isConnected = agents.some((a) => a.auth_status === "authenticated");
  const needsLogin = agents.some((a) => a.auth_status === "needs_login");

  const handleConnect = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("claude-oauth", {
        body: { action: "start" },
      });
      if (error) throw error;

      setCodeVerifier(data.code_verifier);

      // Open Claude login in new tab
      window.open(data.authorize_url, "_blank");

      setStep("paste");
    } catch (err) {
      toast.error(`Failed to start auth: ${formatError(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExchange = async () => {
    if (!pasteCode.trim() || !codeVerifier) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("claude-oauth", {
        body: { action: "exchange", code: pasteCode.trim(), code_verifier: codeVerifier },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      if (!data.success) throw new Error("Exchange failed");

      toast.success(data.pending
        ? "Code sent to Major Tom. It will connect within 30 seconds."
        : "Claude Max connected!");
      setStep("idle");
      setPasteCode("");
      setCodeVerifier(null);
    } catch (err) {
      toast.error(`Failed to connect: ${formatError(err)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Claude Max</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Use your Claude subscription for sessions
              </CardDescription>
            </div>
          </div>
          {isConnected && (
            <Badge variant="outline" className="flex items-center gap-1.5 text-green-500 border-green-500/30">
              <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
              Connected
            </Badge>
          )}
          {needsLogin && !isConnected && (
            <Badge variant="outline" className="flex items-center gap-1.5 text-red-400 border-red-400/30">
              <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              Disconnected
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {step === "idle" && (
          <div className="space-y-3">
            {isConnected ? (
              <p className="text-xs text-muted-foreground">
                Sessions use your Claude Max subscription. No API charges.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Connect your Claude Max account so sessions use your subscription instead of API credits.
              </p>
            )}
            <Button
              size="sm"
              variant={isConnected ? "outline" : "default"}
              onClick={handleConnect}
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin mr-2" />
              ) : (
                <Link2 className="h-3 w-3 mr-2" />
              )}
              {isConnected ? "Reconnect Claude Max" : "Connect Claude Max"}
            </Button>
          </div>
        )}

        {step === "paste" && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              A Claude login page opened in a new tab. Sign in, then copy the code shown and paste it below.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={pasteCode}
                onChange={(e) => setPasteCode(e.target.value)}
                placeholder="Paste your code here..."
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleExchange();
                }}
              />
              <Button size="sm" onClick={handleExchange} disabled={loading || !pasteCode.trim()}>
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              </Button>
            </div>
            <button
              onClick={() => { setStep("idle"); setPasteCode(""); setCodeVerifier(null); }}
              className="text-[10px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MacMiniSetupSection() {
  const steps = [
    "Create a bot email (e.g., agent@hellaorganized.life)",
    "Run the provisioning script on a fresh Mac Mini",
    "Authenticate GitHub and Claude CLI",
    "Configure .env with Supabase credentials",
    "Start the agent with PM2",
    "Check Agent Status above shows \"Online\"",
  ];

  return (
    <section className="mb-6">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
        Mac Mini Agent
      </h2>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Terminal className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Mac Mini Setup</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Set up an always-on AI agent on a Mac Mini
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="list-decimal list-inside space-y-2">
            {steps.map((step, i) => (
              <li key={i} className="text-sm text-muted-foreground">
                {step}
              </li>
            ))}
          </ol>
          <a
            href="https://github.com/HELLA-Intelligence/hella-organized/blob/master/agent-runner/README.md"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" size="sm">
              View Full Guide
            </Button>
          </a>
        </CardContent>
      </Card>
    </section>
  );
}

function BrandColorField({
  label,
  description,
  storageKey,
  cssVar,
  cssVarFg,
  presets,
  defaultValue,
}: {
  label: string;
  description: string;
  storageKey: string;
  cssVar: string;
  cssVarFg?: string;
  presets?: string[];
  defaultValue?: string;
}) {
  const [color, setColor] = useState(() => localStorage.getItem(storageKey) || defaultValue || "");
  const [input, setInput] = useState(color);

  function hexToHsl(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  }

  function apply(hex: string) {
    if (hex && /^#[0-9a-fA-F]{6}$/.test(hex)) {
      localStorage.setItem(storageKey, hex);
      document.documentElement.style.setProperty(cssVar, hexToHsl(hex));
      if (cssVarFg) {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        document.documentElement.style.setProperty(cssVarFg, luma > 128 ? "0 0% 10%" : "0 0% 98%");
      }
    } else {
      localStorage.removeItem(storageKey);
      document.documentElement.style.removeProperty(cssVar);
      if (cssVarFg) document.documentElement.style.removeProperty(cssVarFg);
    }
  }

  useEffect(() => {
    apply(color);
  }, [color]);

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) apply(saved);
  }, []);

  return (
    <div className="space-y-2">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {presets && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {presets.map((hex) => (
            <button
              key={hex}
              onClick={() => { setColor(hex); setInput(hex); }}
              className="relative h-7 w-7 rounded-full border-2 transition-all flex items-center justify-center shrink-0"
              style={{
                backgroundColor: hex,
                borderColor: color.toLowerCase() === hex.toLowerCase() ? "white" : "transparent",
                boxShadow: color.toLowerCase() === hex.toLowerCase() ? `0 0 0 2px ${hex}` : "none",
              }}
            >
              {color.toLowerCase() === hex.toLowerCase() && (
                <Check className="h-3 w-3 text-white drop-shadow-md" />
              )}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <label className="relative h-8 w-8 rounded-md border border-border shrink-0 cursor-pointer overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ backgroundColor: color || "transparent" }}
          />
          <input
            type="color"
            value={color || "#000000"}
            onChange={(e) => {
              setColor(e.target.value);
              setInput(e.target.value);
            }}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          />
        </label>
        <Input
          className="h-8 text-sm font-mono flex-1"
          placeholder="#000000"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) setColor(e.target.value);
          }}
          onBlur={() => {
            if (/^#[0-9a-fA-F]{6}$/.test(input)) setColor(input);
            else if (input === "") setColor("");
            else setInput(color);
          }}
        />
        {color && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs text-muted-foreground"
            onClick={() => { setColor(""); setInput(""); }}
          >
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}

function BrandKitCard() {
  const { style } = useCommandStyle();
  const c = style.colors;
  const primaryPresets = ["#3b82f6", "#8b5cf6", "#ec4899", "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4"];

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-muted">
            <Palette className="h-5 w-5" />
          </div>
          <div>
            <CardTitle className="text-base">Brand Kit</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Customize your command center colors
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <BrandColorField
          label="Primary Color"
          description="Buttons, links, highlights, active states"
          storageKey="ho-accent-color"
          cssVar="--primary"
          cssVarFg="--primary-foreground"
          presets={primaryPresets}
          defaultValue={c.primary}
        />
        <BrandColorField
          label="Background"
          description="Main page background"
          storageKey="ho-brand-bg"
          cssVar="--background"
          defaultValue={c.background}
        />
        <BrandColorField
          label="Card Background"
          description="Cards, panels, elevated surfaces"
          storageKey="ho-brand-card"
          cssVar="--card"
          defaultValue={c.surface}
        />
        <BrandColorField
          label="Sidebar"
          description="Navigation sidebar background"
          storageKey="ho-brand-sidebar"
          cssVar="--sidebar-background"
          defaultValue={c.background}
        />
        <BrandColorField
          label="Border"
          description="Borders, dividers, input outlines"
          storageKey="ho-brand-border"
          cssVar="--border"
          defaultValue={c.border}
        />
      </CardContent>
    </Card>
  );
}

function PlaudCard({ existing }: { existing: Integration | undefined }) {
  const { user } = useAuth();
  const deleteIntegration = useDeleteIntegration();
  const sync = useSyncIntegration();
  const [connecting, setConnecting] = useState(false);

  const isConnected = !!existing?.enabled;
  // Plaud refresh tokens only live 7 days — if we haven't synced in 3+
  // days the stored token is almost certainly dead and only a fresh OAuth
  // (Reconnect) fixes it. Don't show a green "Connected" lie.
  const stale =
    isConnected &&
    (!existing?.last_synced_at ||
      Date.now() - new Date(existing.last_synced_at).getTime() > 3 * 86400_000);

  const handleConnect = async () => {
    if (!user) return;
    setConnecting(true);
    // Plaud OAuth flow — closes adversarial-breaker F1.13 (Phase 1 iter 1,
    // 2026-05-17). The server-side plaud-auth fn mints an HMAC-signed state
    // token bound to the authed user_id; plaud-callback rejects callbacks
    // that don't carry a valid signed state.
    try {
      const { data, error } = await supabase.functions.invoke("plaud-auth", {
        body: {},
      });
      if (error) throw error;
      if (!data?.url) throw new Error("plaud-auth did not return a URL");
      window.location.href = data.url;
    } catch (err) {
      toast.error(`Plaud connect failed: ${formatError(err)}`);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!existing) return;
    try {
      await deleteIntegration.mutateAsync(existing.id);
      toast.success("Plaud disconnected");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const handleSync = async () => {
    try {
      const result = await sync.mutateAsync("plaud");
      if (result?.error) {
        toast.error(result.error);
      } else {
        toast.success(
          `Plaud synced${result?.items_created != null ? ` (${result.items_created} items created)` : ""}`
        );
      }
    } catch (err) {
      // FunctionsHttpError hides the response body behind .context — pull
      // the real error out so "token expired" doesn't read as a generic
      // "non-2xx status code" toast.
      let message = formatError(err);
      const ctx = (err as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") {
        try {
          const body = await ctx.clone().json();
          if (body?.error) message = body.error;
        } catch {
          // keep the generic message
        }
      }
      if (/expired|refresh failed/i.test(message)) {
        toast.error("Plaud login expired — hit Reconnect to sign in again.");
      } else {
        toast.error(`Sync failed: ${message}`);
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Headphones className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Plaud</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Auto-extract action items from call recordings
              </CardDescription>
            </div>
          </div>
          {isConnected && (
            <Badge
              variant="outline"
              className={
                stale
                  ? "text-xs text-yellow-500 border-yellow-500/30"
                  : "text-xs text-green-500 border-green-500/30"
              }
            >
              {stale ? "Reconnect needed" : "Connected"}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isConnected ? (
          <>
            <div className="text-xs text-muted-foreground">
              {existing?.last_synced_at
                ? `Last synced ${formatDistanceToNow(new Date(existing.last_synced_at), { addSuffix: true })}`
                : "Never synced"}
              {stale && (
                <span className="block text-yellow-500/90 mt-0.5">
                  Plaud sign-in expires after a week of no syncing — reconnect to resume.
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={stale ? "default" : "outline"}
                onClick={handleConnect}
                disabled={connecting}
              >
                {connecting ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Plus className="h-3 w-3 mr-1" />
                )}
                Reconnect
              </Button>
              <Button size="sm" variant="outline" onClick={handleSync} disabled={sync.isPending}>
                {sync.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                Sync Now
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-muted-foreground hover:text-destructive"
                onClick={handleDisconnect}
                disabled={deleteIntegration.isPending}
              >
                Disconnect
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Plus className="h-3 w-3 mr-1" />
              )}
              Connect Plaud
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Don't have a Plaud?{" "}
              <a
                href="https://plaudus.sjv.io/GbmMjV"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Get one here
              </a>
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: integrations, isLoading } = useIntegrations();
  const { theme, toggleTheme } = useTheme();
  const { copy } = useCommandStyle();
  const [muted, setMutedState] = useState(getMuted());

  // Accent color
  // Handle OAuth callback query params
  useEffect(() => {
    const gmailConnected = searchParams.get("gmail_connected");
    const gmailError = searchParams.get("gmail_error");
    const githubConnected = searchParams.get("github_connected");
    const githubError = searchParams.get("github_error");
    if (gmailConnected) {
      toast.success(`Connected ${gmailConnected}`);
      setSearchParams({}, { replace: true });
    } else if (gmailError) {
      toast.error(`Gmail connection failed: ${gmailError}`);
      setSearchParams({}, { replace: true });
    } else if (githubConnected) {
      toast.success("GitHub connected successfully");
      setSearchParams({}, { replace: true });
    } else if (githubError) {
      toast.error(`GitHub connection failed: ${githubError}`);
      setSearchParams({}, { replace: true });
    } else if (searchParams.get("plaud_connected")) {
      toast.success("Plaud connected — recordings will sync within a couple hours");
      setSearchParams({}, { replace: true });
    } else if (searchParams.get("plaud_error")) {
      toast.error(`Plaud connection failed (${searchParams.get("plaud_error")}) — try Reconnect again`);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const gmailAccounts = integrations?.filter((i) => i.type === "gmail") || [];
  const getExisting = (type: string) =>
    integrations?.find((i) => i.type === type);

  return (
    <div
      className="h-dvh bg-background text-foreground flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold">{copy.settings}</h1>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-4 py-4 max-w-2xl mx-auto w-full">
        {/* Command Style */}
        <section className="mb-6">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Command Style
          </h2>
          <CommandStyleSelector />
        </section>

        {/* Look & Feel */}
        <section className="mb-6">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Look &amp; Feel
          </h2>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted">
                    {theme === "dark" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                  </div>
                  <div>
                    <CardTitle className="text-base">Theme</CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      {theme === "dark" ? "Dark mode" : "Light mode"}
                    </CardDescription>
                  </div>
                </div>
                <Switch
                  checked={theme === "dark"}
                  onCheckedChange={toggleTheme}
                />
              </div>
            </CardHeader>
          </Card>
          <Card className="mt-4">
            <CardHeader>
              <div className="flex items-center justify-between py-3">
                <div>
                  <h3 className="text-sm font-medium">Sound Effects</h3>
                  <p className="text-xs text-muted-foreground">Completion sounds, level ups, focus timer</p>
                </div>
                <button
                  onClick={() => {
                    const next = !muted;
                    setMuted(next);
                    setMutedState(next);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                    muted
                      ? "border-border text-muted-foreground"
                      : "border-primary text-primary bg-primary/10"
                  }`}
                >
                  {muted ? "OFF" : "ON"}
                </button>
              </div>
            </CardHeader>
          </Card>
          <BrandKitCard />
        </section>

        {/* Setup Progress */}
        {!isLoading && (
          <section className="mb-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Setup Progress</CardTitle>
                <CardDescription className="text-xs">
                  Connect your tools to get the most out of your command center
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    { label: "Gmail & Calendar", done: gmailAccounts.length > 0 },
                    { label: "GitHub (repo mapping)", done: !!getExisting("github")?.enabled },
                    { label: "Fireflies (meeting transcripts)", done: !!getExisting("fireflies")?.enabled },
                    { label: "Plaud (call recordings)", done: !!getExisting("plaud")?.enabled },
                  ].map((step) => (
                    <div key={step.label} className="flex items-center gap-2">
                      {step.done ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                      ) : (
                        <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                      )}
                      <span className={`text-sm ${step.done ? "text-foreground" : "text-muted-foreground"}`}>
                        {step.label}
                      </span>
                      {step.done && (
                        <Badge variant="outline" className="text-[10px] text-green-500 border-green-500/30 ml-auto">
                          Connected
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        <section className="mb-6">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Integrations
          </h2>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              <GmailCard gmailAccounts={gmailAccounts} />
              {OTHER_INTEGRATIONS.map((def) => (
                <IntegrationCard
                  key={def.type}
                  def={def}
                  existing={getExisting(def.type)}
                />
              ))}
              <PlaudCard existing={getExisting("plaud")} />
              <BeeperCard existing={getExisting("beeper")} />
            </div>
          )}
        </section>

        {/* AI Accounts section removed — Claude Max connection is now in Agent section */}

        <section className="mb-6">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Developer Tools
          </h2>
          <div className="space-y-4">
            {DEV_INTEGRATIONS.map((def) => (
              <IntegrationCard key={def.type} def={def} existing={getExisting(def.type)} />
            ))}
          </div>
        </section>

        <BillingSection />

        <WebhooksSection />

        <CustomConnectionsSection />

        <ContactsSection />

        <AgentSection />

        <MacMiniSetupSection />
      </main>
    </div>
  );
}
